import { useParams, Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import Reveal from "@/components/Reveal";
import { ArrowLeft, Leaf, ChevronRight } from "lucide-react";
import { useProduct } from "@/hooks/useCatalogue";
import { BRAND } from "@/lib/brand";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

function InfoRow({ label, value }) {
  return (
    <div className="py-4 border-b border-forest/15 flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-6">
      <dt className="text-[10px] uppercase tracking-[0.24em] text-moss sm:w-40 shrink-0">
        {label}
      </dt>
      <dd className="text-charcoal/80">{value}</dd>
    </div>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { data: product, isLoading, isError } = useProduct(slug);
  const [activeImg, setActiveImg] = useState(0);

  if (isLoading) {
    return <div className="max-w-7xl mx-auto px-4 py-24"><div className="h-96 bg-linen animate-pulse" /></div>;
  }
  if (isError || !product) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-28 text-center">
        <p className="font-serif text-3xl text-forest">Product not found</p>
        <Link to="/products" className="mt-4 inline-block text-forest underline underline-offset-4">Back to Our Rituals</Link>
      </div>
    );
  }

  const images = [product.image, ...(product.gallery || [])];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
      <button
        onClick={() => navigate("/products")}
        className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-charcoal/55 hover:text-forest mb-10 transition-colors"
      >
        <ArrowLeft size={14} /> Our Rituals
      </button>

      <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
        {/* GALLERY */}
        <div>
          <div className="frame">
            <div className="relative overflow-hidden bg-linen aspect-[4/5]">
              <img
                key={activeImg}
                src={images[activeImg]}
                alt={product.attribution}
                data-testid="product-main-image"
                className={`w-full h-full animate-fade-up ${product.image_placeholder ? "object-contain p-12 mix-blend-multiply" : "object-cover"}`}
              />
              {product.image_placeholder && (
                <span className="absolute top-5 left-5 text-[10px] uppercase tracking-[0.22em] text-forest/50">
                  Placeholder imagery
                </span>
              )}
            </div>
          </div>
          {images.length > 1 && (
            <div className="mt-4 flex gap-3">
              {images.map((img, i) => (
                <button
                  key={img}
                  onClick={() => setActiveImg(i)}
                  data-testid={`product-thumb-${i}`}
                  aria-label={`View image ${i + 1}`}
                  className={`w-20 h-24 overflow-hidden bg-linen border transition-colors ${
                    activeImg === i ? "border-forest" : "border-forest/15 hover:border-forest/50"
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* DETAILS */}
        <div>
          <p className="eyebrow text-champagne">
            {product.routine_role} · {BRAND.system}
          </p>
          <h1 className="mt-5 font-serif font-light text-5xl sm:text-6xl lg:text-7xl text-forest tracking-tight leading-[0.95]">
            {product.name}
          </h1>
          <p className="mt-3 text-[11px] uppercase tracking-[0.22em] text-charcoal/45">{product.attribution}</p>
          <p className="mt-8 text-lg leading-relaxed text-charcoal/70 font-light">
            {product.short_description}
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link
              to="/join"
              data-testid="product-join-cta"
              className="inline-flex items-center justify-center gap-2 btn btn-primary"
            >
              {BRAND.joinCta}
            </Link>
            <Link
              to="/community"
              className="inline-flex items-center justify-center gap-2 btn btn-outline"
            >
              Community discussions <ChevronRight size={16} />
            </Link>
          </div>
          <p className="mt-3 text-xs text-charcoal/50">
            No programmes are open for this product yet. Join the community to be invited.
          </p>

          {/* The 3-Way System */}
          <div className="mt-12 border border-forest/15 p-1">
            <div className="border border-forest/10 p-6">
            <div className="flex items-center gap-3 text-forest">
              <Leaf size={16} strokeWidth={1.5} className="text-moss" />
              <h2 className="font-serif text-2xl font-light">The 3-Way System</h2>
            </div>
            <div className="mt-5 grid sm:grid-cols-3 gap-3">
              {(product.pathways || []).map((pw, i) => (
                <div key={pw.pathway_id} className="bg-linen p-5">
                  <p className="text-[10px] uppercase tracking-[0.24em] text-moss">
                    Pathway {i + 1}
                  </p>
                  <p className="mt-2 font-serif text-lg text-forest">To be confirmed</p>
                  <p className="mt-1 text-xs text-charcoal/60">{pw.public_note}</p>
                </div>
              ))}
            </div>
            </div>
          </div>

          {/* Formulation facts */}
          <dl className="mt-10">
            <InfoRow label="Routine role" value={product.routine_role} />
            <InfoRow label="Texture" value={<span className="text-charcoal/60 italic">Formulation details will be shared soon.</span>} />
            <InfoRow label="Ingredients" value={<span className="text-charcoal/60 italic">Full INCI will be shared once approved.</span>} />
            <InfoRow label="Directions" value={product.directions} />
            <InfoRow label="Suitability" value={<span className="text-charcoal/60 italic">To be confirmed.</span>} />
            <InfoRow label="Cautions" value={<span className="text-charcoal/60 italic">To be confirmed.</span>} />
          </dl>
        </div>
      </div>

      {/* FAQ */}
      <Reveal className="max-w-3xl mt-20">
        <p className="eyebrow mb-6">Good to know</p>
        <h2 className="font-serif font-light text-4xl text-forest mb-6">About this ritual</h2>
        <Accordion type="single" collapsible>
          <AccordionItem value="a1" className="border-forest/15">
            <AccordionTrigger className="text-left font-serif text-2xl font-light text-forest hover:no-underline py-6">
              When will full formulation details be available?
            </AccordionTrigger>
            <AccordionContent className="text-charcoal/75 leading-relaxed">
              We only publish approved details. Ingredients, texture and directions will appear
              here once confirmed. We never invent claims or certifications.
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="a2" className="border-forest/15">
            <AccordionTrigger className="text-left font-serif text-2xl font-light text-forest hover:no-underline py-6">
              How can I try {product.name}?
            </AccordionTrigger>
            <AccordionContent className="text-charcoal/75 leading-relaxed">
              Experience programmes open periodically. Join the community and, when a programme
              for this product opens, you can apply—subject to eligibility and availability.
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </Reveal>
    </div>
  );
}
